import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api } from "../axios/api";

const persistSession = ({ user, token, empresa }) => {
  localStorage.setItem("user", JSON.stringify(user));
  localStorage.setItem("token", token);

  if (empresa) {
    localStorage.setItem("empresa", JSON.stringify(empresa));
  } else {
    localStorage.removeItem("empresa");
  }
};

export const loginUser = createAsyncThunk(
  "user/login",
  async ({ email, senha }, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/login", { email, senha });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro no login.");
    }
  }
);

export const registerCompany = createAsyncThunk(
  "user/registerCompany",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/register-company", payload, {
        headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao criar empresa.");
    }
  }
);

export const checkAuth = createAsyncThunk(
  "user/checkAuth",
  async (_, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const token = localStorage.getItem("token");
      const empresa = JSON.parse(localStorage.getItem("empresa"));
      if (user && token) return { user, token, empresa };
      return rejectWithValue("Não autenticado.");
    } catch (err) {
      return rejectWithValue("Erro ao verificar autenticação.");
    }
  }
);

export const logout = createAsyncThunk(
  "user/logout",
  async (_, { rejectWithValue }) => {
    try {
      await api.post("/auth/logout");
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("empresa");
      return true;
    } catch (error) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      localStorage.removeItem("empresa");
      return rejectWithValue(error.message || "Erro no logout.");
    }
  }
);

export const fetchCompanyUsers = createAsyncThunk(
  "user/fetchCompanyUsers",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/auth/users");
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao carregar utilizadores.");
    }
  }
);

export const createCompanyUser = createAsyncThunk(
  "user/createCompanyUser",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/users", payload, {
        headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao cadastrar utilizador.");
    }
  }
);

export const updateCompanyUser = createAsyncThunk(
  "user/updateCompanyUser",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const config = {
        headers: data instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
      };
      const response = data instanceof FormData
        ? await api.post(`/auth/users/${id}`, data, config)
        : await api.put(`/auth/users/${id}`, data, config);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao editar utilizador.");
    }
  }
);

export const deleteCompanyUser = createAsyncThunk(
  "user/deleteCompanyUser",
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`/auth/users/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao excluir utilizador.");
    }
  }
);

export const fetchCompanyProfile = createAsyncThunk(
  "user/fetchCompanyProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/auth/company");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao carregar perfil da empresa.");
    }
  }
);

export const updateCompanyProfile = createAsyncThunk(
  "user/updateCompanyProfile",
  async (payload, { rejectWithValue }) => {
    try {
      const config = {
        headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
      };
      const response = payload instanceof FormData
        ? await api.post("/auth/company", payload, config)
        : await api.put("/auth/company", payload, config);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao actualizar perfil da empresa.");
    }
  }
);

export const fetchUserProfile = createAsyncThunk(
  "user/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/auth/profile");
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao carregar perfil.");
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "user/updateUserProfile",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post("/auth/profile", payload, {
        headers: payload instanceof FormData ? { "Content-Type": "multipart/form-data" } : undefined,
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.message || "Erro ao actualizar perfil.");
    }
  }
);

const initialState = {
  user: null,
  token: null,
  empresa: null,
  users: [],
  loading: false,
  error: null,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.empresa = action.payload.empresa || null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.empresa = action.payload.empresa || null;
        persistSession(action.payload);
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(registerCompany.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerCompany.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.empresa = action.payload.empresa || null;
        persistSession(action.payload);
      })
      .addCase(registerCompany.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkAuth.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(checkAuth.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.empresa = action.payload.empresa || null;
      })
      .addCase(checkAuth.rejected, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.empresa = null;
      })
      .addCase(logout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(logout.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.empresa = null;
        state.users = [];
      })
      .addCase(logout.rejected, (state, action) => {
        state.loading = false;
        state.user = null;
        state.token = null;
        state.empresa = null;
        state.users = [];
        state.error = action.payload;
      })
      .addCase(fetchCompanyUsers.fulfilled, (state, action) => {
        state.users = action.payload;
      })
      .addCase(fetchCompanyUsers.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(createCompanyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCompanyUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = [action.payload, ...state.users];
      })
      .addCase(createCompanyUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateCompanyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompanyUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.map((item) => item.id === action.payload.id ? action.payload : item);
        if (state.user?.id === action.payload.id) {
          state.user = action.payload;
          localStorage.setItem("user", JSON.stringify(action.payload));
        }
      })
      .addCase(updateCompanyUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteCompanyUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCompanyUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter((item) => item.id !== action.payload);
      })
      .addCase(deleteCompanyUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchCompanyProfile.fulfilled, (state, action) => {
        state.empresa = action.payload;
        localStorage.setItem("empresa", JSON.stringify(action.payload));
      })
      .addCase(fetchCompanyProfile.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateCompanyProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCompanyProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.empresa = action.payload;
        localStorage.setItem("empresa", JSON.stringify(action.payload));
      })
      .addCase(updateCompanyProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.user = action.payload;
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        localStorage.setItem("user", JSON.stringify(action.payload));
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setUser } = userSlice.actions;
export default userSlice.reducer;
